/**
 * Token Exchange Schedule Service
 *
 * Schedules a single token exchange retry when the browser goes back online
 * after a failed exchange (e.g. due to network). Listener removes itself after firing.
 * Used by PersonStore when exchangeForBVToken fails with network/unknown.
 *
 * Singleton: use getInstance(rootStore). Only one instance per app so only one 'online' listener is ever active.
 */

import type { RootStore } from 'Stores/RootStore';
import { sendIpcLog } from 'Utils/ipcRendererEvents';
import { bugsnagClient } from 'Utils/logUtils';

export class TokenExchangeScheduleService {
  private static instance: TokenExchangeScheduleService | null = null;

  private rootStore: RootStore;
  private activeHandler: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  /**
   * Returns the singleton instance. Creates it on first call.
   */
  static getInstance(rootStore: RootStore): TokenExchangeScheduleService {
    if (!TokenExchangeScheduleService.instance) {
      TokenExchangeScheduleService.instance = new TokenExchangeScheduleService(
        rootStore
      );
    }
    return TokenExchangeScheduleService.instance;
  }

  /**
   * Schedules a token exchange retry when the browser goes online.
   * If already online, runs the exchange immediately. Otherwise adds a one-shot 'online' listener.
   */
  schedule(): void {
    bugsnagClient.leaveBreadcrumb(
      '[Token Exchange] Scheduling token exchange retry when browser is back online'
    );

    if (typeof window === 'undefined' || this.activeHandler) {
      bugsnagClient.leaveBreadcrumb(
        '[Token Exchange] Scheduling token exchange retry failed, window is undefined or active handler already exists'
      );
      return;
    }

    if (navigator.onLine) {
      bugsnagClient.leaveBreadcrumb(
        '[Token Exchange] Browser is online, running token exchange retry immediately'
      );
      void this.handleOnline();
      return;
    }

    bugsnagClient.leaveBreadcrumb(
      '[Token Exchange] Browser is offline, adding online event listener'
    );
    this.activeHandler = this.handleOnline.bind(this) as () => void;
    window.addEventListener('online', this.activeHandler);
  }

  /**
   * Handles the online event by exchanging the token and logging in.
   */
  private async handleOnline(): Promise<void> {
    bugsnagClient.leaveBreadcrumb('[Token Exchange] Handling online event');

    if (typeof window !== 'undefined' && this.activeHandler) {
      window.removeEventListener('online', this.activeHandler);
      this.activeHandler = null;
    }

    const { personStore } = this.rootStore;
    try {
      const token = await personStore.exchangeForBVToken({ ignoreCache: true });
      if (token) {
        await personStore.loginWithExistingToken(token, false);
        bugsnagClient.leaveBreadcrumb(
          '[Token Exchange] Token exchange succeeded after online event'
        );
        sendIpcLog('info', 'Window was automatically reloaded', {
          reason: 'token exchange schedule service',
        });
        window.location.reload();
      } else {
        bugsnagClient.leaveBreadcrumb(
          '[Token Exchange] No token after online event, logging out'
        );
        await personStore.initiateAutoLogout(true);
      }
    } catch {
      bugsnagClient.leaveBreadcrumb(
        '[Token Exchange] Token exchange retry on online failed, logging out'
      );
      await personStore.initiateAutoLogout(true);
    }
  }
}
